import type { ReactNode } from 'react';
import { NavLink } from 'react-router';

import { cn } from '@psspl/utils';

import { ROUTES } from '../../lib/routes';

export interface NavigationItem {
  readonly to: string;
  readonly label: string;
  readonly icon: ReactNode;
  /**
   * Match the path exactly. Needed for index routes such as `/admin`, which would
   * otherwise stay highlighted on every `/admin/*` screen.
   */
  readonly end?: boolean;
}

const iconClass = 'size-4 shrink-0';

export const PROJECTS_ICON = (
  <svg
    viewBox="0 0 20 20"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path d="M2.5 6.5 10 2.5l7.5 4v7L10 17.5 2.5 13.5v-7Z" strokeLinejoin="round" />
    <path d="M2.5 6.5 10 10.5l7.5-4M10 10.5v7" strokeLinejoin="round" />
  </svg>
);

export const ANALYTICS_ICON = (
  <svg
    viewBox="0 0 20 20"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path d="M3 3v14h14" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m6 12 3-3.5 2.5 2L16 5.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ACCESS_LOGS_ICON = (
  <svg
    viewBox="0 0 20 20"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <circle cx="10" cy="10" r="7" />
    <path d="M10 6v4.2l2.6 1.6" strokeLinecap="round" />
  </svg>
);

export const PROFILE_ICON = (
  <svg
    viewBox="0 0 20 20"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <circle cx="10" cy="7" r="3" />
    <path d="M4 16.5c.9-2.6 3.2-4 6-4s5.1 1.4 6 4" strokeLinecap="round" />
  </svg>
);

export const ADMIN_ICON = (
  <svg
    viewBox="0 0 20 20"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path
      d="M10 2.5 3 5.5v4.2c0 3.6 2.8 6.5 7 7.8 4.2-1.3 7-4.2 7-7.8V5.5l-7-3Z"
      strokeLinejoin="round"
    />
    <path d="M7.4 10.1 9.2 12l3.4-3.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const USER_MANAGEMENT_ICON = (
  <svg
    viewBox="0 0 20 20"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <circle cx="7.5" cy="7" r="2.6" />
    <circle cx="14" cy="8.2" r="2" />
    <path
      d="M2.8 16c.7-2.2 2.6-3.4 4.7-3.4s4 1.2 4.7 3.4M12.6 13.2c1.7 0 3.4.9 4.1 2.6"
      strokeLinecap="round"
    />
  </svg>
);

export const PERMISSIONS_ICON = (
  <svg
    viewBox="0 0 20 20"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <rect x="4" y="8.5" width="12" height="8.5" rx="1.6" />
    <path d="M6.8 8.5V6.2a3.2 3.2 0 1 1 6.4 0v2.3" strokeLinecap="round" />
  </svg>
);

export const LOGOUT_ICON = (
  <svg
    viewBox="0 0 20 20"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path d="M8 3.5H4.5A1.5 1.5 0 0 0 3 5v10a1.5 1.5 0 0 0 1.5 1.5H8" strokeLinecap="round" />
    <path d="M12.5 6.5 16 10l-3.5 3.5M7.8 10H16" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export interface NavLinkItemProps {
  readonly item: NavigationItem;
  readonly className?: string | undefined;
  readonly onNavigate?: (() => void) | undefined;
  readonly children?: ReactNode;
}

/**
 * One navigation entry.
 *
 * `NavLink` gives `aria-current="page"` for free, so the active item is not
 * signalled by colour alone — it also carries weight and an underline rule.
 */
export function NavLinkItem({ item, className, onNavigate, children }: NavLinkItemProps) {
  return (
    <NavLink
      to={item.to}
      end={item.end === true}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'duration-150 ease-out-standard',
          isActive
            ? 'bg-surface-raised text-ink-primary shadow-[inset_2px_0_0_0_var(--color-brand-500)]'
            : 'text-ink-secondary hover:bg-surface-raised hover:text-ink-primary',
          className,
        )
      }
    >
      {item.icon}
      <span className="truncate">{item.label}</span>
      {children}
    </NavLink>
  );
}

/**
 * The navigation for BD members and clients: the projects the API lists for them,
 * and their profile. Never the full catalogue — `GET /projects` returns only what
 * the caller may see (`docs/api.md` §5.2).
 */
export const USER_NAVIGATION: readonly NavigationItem[] = [
  { to: ROUTES.projects, label: 'My Projects', icon: PROJECTS_ICON },
  { to: ROUTES.profile, label: 'Profile', icon: PROFILE_ICON },
];
